import { motion } from "framer-motion";
import { Eye, Flame, Globe2, Infinity as InfinityIcon } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const CARDS = [
  {
    icon: Eye,
    title: "Attention Monopoly",
    body: "Trump-grade headline gravity fused with cat-meme shareability. $TATE is engineered to dominate every feed it touches — because in this market, attention is everything.",
  },
  {
    icon: Globe2,
    title: "Cross-Cultural Bridge",
    body: "Political memes meet animal memes. Two of the internet's biggest meme economies collide under one ticker, pulling in communities that never shared a chart before.",
  },
  {
    icon: Flame,
    title: "Relentless Conviction",
    body: "A community-first brand built on humor and unapologetic energy. $TATE gives holders a reason to keep showing up, creating, and spreading — long after the hype cycle ends.",
  },
  {
    icon: InfinityIcon,
    title: "Infinite Content Engine",
    body: "Rallies, debates, cat chaos — the narrative writes itself daily. The strongest memes don't follow trends. They create them.",
  },
];

export default function MarketImpact() {
  return (
    <section id="impact" className="relative py-28 sm:py-36" data-testid="impact-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[#0F1E36] opacity-40 blur-[120px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]"
        >
          03 / Why It Hits
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
          className="mt-5 max-w-3xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
        >
          Why $TATE moves the
          <br />
          <span className="text-stroke-gold">meme community.</span>
        </motion.h2>

        <div className="mt-16 grid gap-5 sm:grid-cols-2">
          {CARDS.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, ease: EASE, delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
                e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
              }}
              className="group relative overflow-hidden rounded-2xl border border-[rgba(229,169,60,0.18)] bg-[#0E1015] p-8 transition-colors duration-300 hover:border-[rgba(246,200,95,0.6)] sm:p-10"
              data-testid={`impact-card-${i + 1}`}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(320px circle at var(--mx, 50%) var(--my, 50%), rgba(246,200,95,0.13), transparent 60%)",
                }}
              />
              <span className="pointer-events-none absolute -right-4 -top-6 font-display text-[7rem] font-black leading-none text-[rgba(229,169,60,0.07)] transition-colors duration-500 group-hover:text-[rgba(229,169,60,0.14)]">
                0{i + 1}
              </span>
              <div className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(229,169,60,0.35)] bg-[#07080A] transition-transform duration-300 group-hover:scale-110">
                <c.icon className="h-5 w-5 text-[#E5A93C]" />
              </div>
              <h3 className="relative mt-7 font-display text-xl font-extrabold uppercase tracking-tight sm:text-2xl">
                {c.title}
              </h3>
              <p className="relative mt-3.5 max-w-md text-sm leading-[1.8] text-[#94A3B8] sm:text-base">
                {c.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
